import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { Check, Copy, ExternalLink, FolderPlus, X } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { prettifyUrl } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { Modal } from '../../../Shared/Modal/Modal';
import { Event, Project } from '../../../Shared/types';
import { AButton, Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import { useCopy } from '../../../Shared/useCopy';
import { useRouter } from '../../router';
import { SharedProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import ProjectIcon from '../Projects/ProjectIcon';
import ProjectLogo from '../Projects/ProjectLogo';
import { SettingsLayout } from '../SettingsLayout';
import CreateProjectForm from './CreateProjectForm';
import EventLogo from './EventLogo';

export interface EventPageProps extends SharedProps {
  event: Event & {
    project: Project | null;
  };
  campaignsCount: number;
  donationsCount: number;
  fundsRaised: number;
  defaultProject: Project;
}

export default function EventPage({
  event,
  defaultProject,
  campaignsCount,
  donationsCount,
  fundsRaised,
  charity,
}: EventPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { formatCurrency, formatDate, formatNumber, locale } = useLocale();

  const createCampaignUrl = routes['website.create_campaign_page'](charity.id, { eventId: event.id });
  const [copyCreateCampaignUrl, copiedCreateCampaignUrl] = useCopy(createCampaignUrl);

  const [showModal, setShowModal] = useState(false);

  const project = event.project || defaultProject;

  return (
    <>
      <Header>
        <Container>
          <div className="flex items-center justify-between">
            <HeaderTitle className={classNames(event.project && !event.project.enabled && 'opacity-50')}>
              <span className="mr-3">
                <EventLogo event={event} size="lg" />
              </span>
              {event.title}
            </HeaderTitle>
            <a
              href={routes['website.home_page'](locale, charity.slug, event.slug)}
              target="_blank"
              rel="noopener noreferrer"
              data-supporta-embed="false"
              className="font-medium"
            >
              {t('backend:visit')}
              <Icon className="ml-1">
                <ExternalLink />
              </Icon>
            </a>
          </div>
        </Container>
      </Header>

      <Container>
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-3 space-y-4 text-slate-600" style={{ minWidth: 0 }}>
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:date')}
              </h4>
              {formatDate(event.start_date)}
            </div>

            {event.website && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:website')}
                </h4>
                <a
                  href={event.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="block overflow-hidden whitespace-nowrap text-ellipsis">
                    {prettifyUrl(event.website)}
                  </span>
                </a>
              </div>
            )}

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:organiser')}
              </h4>
              {event.organiser}
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:project')}
              </h4>
              <Link href={routes.project_page(project.id)}>
                <ProjectIcon project={project} className="mr-1" />
                {project.title}
              </Link>
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:campaigns')}
              </h4>
              {formatNumber(campaignsCount)}
              {campaignsCount > 0 && (
                <span className="text-slate-400">
                  {' · '}
                  <Link href={`${routes.campaigns_page()}?event_id=${event.id}`}>
                    {t('backend:view')}
                  </Link>
                </span>
              )}
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:donations')}
              </h4>
              {formatNumber(donationsCount)}
              {donationsCount > 0 && (
                <span className="text-slate-400">
                  {' · '}
                  <Link href={`${routes.donations_page()}?event_id=${event.id}`}>
                    {t('backend:view')}
                  </Link>
                </span>
              )}
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:raised')}
              </h4>
              {formatCurrency(fundsRaised)}
            </div>
          </div>

          <div className="col-span-9 space-y-6">
            <Card panels header>
              <div>
                <h3>
                  {t('backend:how_can_campaigns_be_started')}
                </h3>
              </div>
              <div className="grid grid-cols-[28px_1fr] gap-4">
                <div>
                  <div
                    className={classNames(
                      'flex items-center justify-center w-7 h-7 -my-0.5 text-sm rounded-full',
                      event.fundraising_enabled ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50'
                    )}
                  >
                    <Icon>
                      {event.fundraising_enabled && <Check />}
                      {!event.fundraising_enabled && <X />}
                    </Icon>
                  </div>
                </div>
                <div>
                  <strong className={classNames(!event.fundraising_enabled && 'opacity-50')}>
                    {t('backend:through_atleta')}
                  </strong>
                  <div>
                    {event.fundraising_enabled && t('backend:through_atleta_enabled_description', { charity: charity.title })}
                    {!event.fundraising_enabled && t('backend:through_atleta_disabled_description', { charity: charity.title })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-[28px_1fr] gap-4">
                <div>
                  <div
                    className="flex items-center justify-center w-7 h-7 -my-0.5 text-sm rounded-full text-emerald-600 bg-emerald-50"
                  >
                    <Icon>
                      <Check />
                    </Icon>
                  </div>
                </div>
                <div>
                  <strong>
                    {t('backend:through_supporta')}
                  </strong>
                  <div className="mb-2">
                    {t('backend:through_supporta_description')}
                  </div>
                  <div className="flex space-x-2">
                    <Input
                      type="text"
                      value={createCampaignUrl}
                      readOnly
                      onFocus={(event) => event.target.select()}
                    />
                    <Button
                      onClick={() => copyCreateCampaignUrl()}
                      variant="secondary"
                      className={classNames(copiedCreateCampaignUrl && '!text-slate-400')}
                      title={t('backend:copy_signup_link')}
                    >
                      <Icon className="mr-1">
                        {copiedCreateCampaignUrl ? <Check /> : <Copy />}
                      </Icon>
                      {t('backend:copy')}
                    </Button>
                    <AButton
                      href={createCampaignUrl}
                      target="_blank" rel="noopener noreferrer"
                      variant="secondary"
                      data-supporta-embed="false"
                      title={t('backend:open_signup_link')}
                    >
                      <Icon className="mr-1">
                        <ExternalLink />
                      </Icon>
                      {t('backend:open')}
                    </AButton>
                  </div>
                </div>
              </div>
            </Card>

            <Card panels header>
              <div>
                <h3>
                  {t('backend:where_do_campaigns_end_up')}
                </h3>
              </div>
              <div className="space-y-2">
                <p>
                  {t('backend:event_campaigns_are_added_to_project')}
                </p>
                <p className="flex justify-between items-center">
                  <Link href={routes.project_page(project.id)} className="flex items-center space-x-2 font-medium text-slate-800 hover:text-slate-600">
                    <ProjectLogo project={project} charity={charity} />
                    <span>
                      {project.title}
                    </span>
                  </Link>
                  {!event.project && (
                    <Button onClick={() => setShowModal(true)} variant="secondary">
                      <Icon className="mr-1">
                        <FolderPlus />
                      </Icon>
                      {t('backend:create_new_project')}
                    </Button>
                  )}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </Container>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="sm">
        <CreateProjectForm
          event={event}
          campaignsCount={campaignsCount}
          onSuccess={() => setShowModal(false)}
        />
      </Modal>
    </>
  );
}

EventPage.layout = (page: ReactNode) => <Layout><SettingsLayout>{page}</SettingsLayout></Layout>;
